/**
 * User Onboarding Wizard
 *
 * Self-contained wizard for standard user invitation onboarding.
 * Handles its own API calls, steps, and completion logic.
 */

import React, { useCallback, useState } from 'react';
import { DevicePairingDialog } from '@/components/device-pairing';
import { startOnboardingFlow } from '@/features/onboarding/api/onboarding-api';
import { CompleteStep } from '@/features/onboarding/components/steps/complete-step';
import { FailedStep } from '@/features/onboarding/components/steps/failed-step';
import { WelcomeStep } from '@/features/onboarding/components/steps/welcome-step';
import { FactorSteps } from '@/features/onboarding/components/wizards/shared/factor-steps';
import { defaultShouldAutoComplete } from '@/features/onboarding/hooks/use-factor-actions';
import { useOnboardingFlow } from '@/features/onboarding/hooks/use-onboarding-flow';
import {
  FactorType,
  FlowStatus,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';

interface UserOnboardingWizardProps {
  token: string;
  userName?: string;
  onComplete: () => void;
  isMobileOnboardingEligible?: boolean;
  tenantSlug?: string;
}

/**
 * User Onboarding Wizard
 *
 * Flow:
 * 1. Welcome step → calls startOnboarding API
 * 2. Factor steps (SMS, Password, TOTP, etc.)
 * 3. Device pairing dialog (skippable, only if NewCore App was NOT enrolled)
 * 4. Complete step → redirects to /dashboard
 */
export const UserOnboardingWizard: React.FC<UserOnboardingWizardProps> = ({
  token,
  userName,
  onComplete,
  isMobileOnboardingEligible,
  tenantSlug,
}) => {
  // Local UX detour: when the flow reaches the "ready to complete" state but
  // push notification was never enrolled, offer device pairing BEFORE firing
  // /complete. /complete clears the onboarding session cookie, so the
  // dialog's session-authenticated /onboarding/device/start-pair call must
  // happen first. Tracked locally so the shared hook stays flow-agnostic —
  // the invitation wizard reuses the hook without this detour.
  // TODO: consider handling this from the backend
  const [hasHandledDevicePairing, setHasHandledDevicePairing] = useState(false);

  const willEnrollPush = useCallback(
    (response: OnboardingFlowResponse): boolean =>
      (response.factors ?? []).some(
        factor =>
          factor.factorType === FactorType.PUSH_NOTIFICATION &&
          factor.isEnrolled
      ),
    []
  );

  const shouldAutoComplete = useCallback(
    (response: OnboardingFlowResponse): boolean => {
      if (!defaultShouldAutoComplete(response)) {
        return false;
      }

      return willEnrollPush(response);
    },
    [willEnrollPush]
  );

  const flow = useOnboardingFlow({
    token,
    startApi: startOnboardingFlow,
    shouldAutoComplete,
  });

  const dismissDevicePairing = useCallback(() => {
    setHasHandledDevicePairing(true);
    void flow.handleFinalComplete();
  }, [flow]);

  const handleFinalComplete = useCallback(async () => {
    await flow.handleFinalComplete();
    onComplete();
  }, [flow, onComplete]);

  // Welcome step
  if (!flow.state.hasStarted) {
    return (
      <WelcomeStep
        userName={userName ?? ''}
        onGetStarted={flow.handleGetStarted}
        isLoading={flow.state.isLoading}
      />
    );
  }

  // Failed step
  if (flow.state.flowStatus === FlowStatus.FAILED) {
    return (
      <FailedStep error={flow.state.error} onClearError={flow.clearError} />
    );
  }

  // Show the device-pairing dialog when the backend signals the flow is
  // ready to complete, push wasn't enrolled, and the user hasn't dismissed
  // it yet. This must fire BEFORE /complete (which would clear the
  // onboarding session cookie that /onboarding/device/start-pair needs).
  const pushEnrolled = flow.state.enrolledFactors.includes(
    FactorType.PUSH_NOTIFICATION
  );

  if (
    flow.state.canComplete &&
    flow.state.currentFactorType === null &&
    !flow.state.hasAlternative &&
    !pushEnrolled &&
    !hasHandledDevicePairing
  ) {
    return (
      <DevicePairingDialog
        isOpen={true}
        onClose={dismissDevicePairing}
        cancelButtonText='Skip'
        isOnboarding={true}
        showIntro={true}
        tenantSlug={tenantSlug}
      />
    );
  }

  // Complete step
  if (flow.state.flowStatus === FlowStatus.COMPLETED) {
    return (
      <CompleteStep
        enrolledFactors={flow.state.enrolledFactors}
        userName={userName}
        isExchanging={flow.state.isExchanging}
        isAuthenticated={flow.state.isAuthenticated}
        exchangeError={flow.state.exchangeError}
        onExchange={flow.exchangeForSession}
        onComplete={handleFinalComplete}
      />
    );
  }

  // Factor steps (shared between all wizards)
  return (
    <FactorSteps
      state={flow.state}
      onSubmitFactor={flow.submitFactor}
      onClearError={flow.clearError}
      onResend={flow.resendSmsCode}
      canResendSms={flow.canResendSms}
      onChooseAlternative={flow.chooseAlternative}
      onBackToAlternatives={flow.goBackToAlternatives}
      onContinue={flow.completeSecondaryFactors}
      isMobileOnboardingEligible={isMobileOnboardingEligible}
      tenantSlug={tenantSlug}
    />
  );
};
